import type { ChatActivityAnswer } from '@app/@types/redux/answer';
import type { OthersOptions, SimulationEmail } from '@app/@types/redux/pageElement';
import type { PageElement } from '@app/@types/redux/project';
import EmailMessenger from '@components/Email';
import NotificationModal from '@components/Projects/TakeProject/Elements/NotificationModal';
import logger from '@libs/log';
import {
  fetchSimulationEmails,
  updateSimulationEmailOpened,
} from '@state/redux/slices/project.slice';
import { useAppDispatch, useAppSelector } from '@state/redux/store';
import { translate } from '@state/utils/helper';
import { useEffect, useState } from 'react';
import inboxIcon from '../../../../../assets/icons/inbox.png';
import './InboxPage.scss';

interface InboxPageProps {
  pageElement: PageElement;
  currentAnswer: ChatActivityAnswer | undefined;
  answer: (answer: ChatActivityAnswer) => void;
}

const InboxPage: React.FC<InboxPageProps> = (props) => {
  const { pageElement } = props;
  const { id } = pageElement;
  const projectId = useAppSelector((state) => state.projects.currentProject.project.id);
  const dispatch = useAppDispatch();
  const userProjectEmails = useAppSelector((state) => state.projects.currentProject.emails);
  const [isLoading, setIsLoading] = useState(true);
  const [showInitialNotification, setShowInitialNotification] = useState(true);
  const [initialUnreadCount, setInitialUnreadCount] = useState(0);

  const markFirstUnreadAsRead = () => {
    const firstUnreadEmail = userProjectEmails.find((email) => !email.attributes.openedAt);
    if (firstUnreadEmail) {
      dispatch(
        updateSimulationEmailOpened({
          projectId,
          simulationEmailId: firstUnreadEmail.attributes.simulationEmail.id.toString(),
        })
      ).catch(() => {
        logger.error('Error updating email opened');
      });
    }
  };

  // Count total unread messages
  const unreadCount = userProjectEmails.filter(
    (email) =>
      !email.attributes.openedAt &&
      email.attributes.simulationEmail.attributes.emailType !== 'manager-feedback-email'
  ).length;

  // Get first unread message with sender name if there's only one unread message
  const singleUnreadMessage =
    unreadCount === 1 ? userProjectEmails.find((email) => !email.attributes.openedAt) : null;
  const senderName = singleUnreadMessage?.attributes.simulationEmail.attributes.senderName;

  // Set initial unread count when loading completes
  useEffect(() => {
    if (!isLoading && userProjectEmails.length > 0) {
      setInitialUnreadCount(unreadCount);
    }
  }, [isLoading, userProjectEmails, unreadCount]);

  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchSimulationEmails({ projectId, pageElementId: id }))
      .catch(() => {
        logger.error('Failed to fetch simulation emails');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleEmailClick = (email: SimulationEmail) => {
    if (!email.attributes.openedAt) {
      dispatch(
        updateSimulationEmailOpened({
          projectId,
          simulationEmailId: email.attributes.simulationEmail.id.toString(),
        })
      ).catch(() => {
        logger.error('Error updating email opened');
      });
    }
  };

  return (
    <>
      <NotificationModal
        isOpen={!isLoading && showInitialNotification && initialUnreadCount > 0}
        toggle={() => {
          setShowInitialNotification(false);
          markFirstUnreadAsRead();
        }}
        icon={inboxIcon}
        title={translate('notificationModal.newMessage')}
        message={
          initialUnreadCount === 1 && senderName
            ? translate('notificationModal.newMessageMessageFrom', { senderName })
            : translate('notificationModal.newMessagesCount', { count: initialUnreadCount })
        }
        onViewMessage={() => {
          setShowInitialNotification(false);
          markFirstUnreadAsRead();
        }}
        buttonIcon="https://cartedo.s3.us-east-2.amazonaws.com/web/499a44c4e3f38aad9848.png"
        buttonText={translate('notificationModal.clickToViewEmail')}
      />
      <EmailMessenger
        emails={userProjectEmails}
        isLoading={isLoading}
        onEmailClick={handleEmailClick}
        filter={(pageElement.attributes.options as OthersOptions).filter ?? ''}
        inboxElementId={id}
      />
    </>
  );
};

export default InboxPage;
